import { LinearGradient } from 'expo-linear-gradient';
import { useSelectedProfile } from 'hooks/useSelectedProfile';
import React, { useEffect } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useAnalytics } from 'services/analytics/useAnalytics';
import { ProgramType } from 'utils/enum';
import { calcSizeForDevice, calcSizeForTv } from 'utils/index';
import { Platform } from 'utils/platform';

import { usePlayer } from './context';

import { Cancel, Next } from 'components/Button/AutoNext';
import FastImage from 'components/FastImage';
import { useProgram } from 'components/Program/context';

let AUTO_NEXT_TIMEOUT: NodeJS.Timeout | undefined;

export function useAutoNext(props: {
  duration: number;
  currentTime: number;
  forceMobileView?: boolean;
}) {
  const program = useProgram();
  const analytics = useAnalytics();
  const { nextTrack, isFullScreen } = usePlayer();
  const { duration, currentTime, forceMobileView } = props;
  const [showNextTrack, setShowNextTrack] = React.useState(false);
  const [autoNextTriggered, setAutoNextTriggered] = React.useState(false);
  const { selectedProfile } = useSelectedProfile();

  useEffect(() => {
    setAutoNextTriggered(false);
    setShowNextTrack(false);
  }, [nextTrack]);

  // auto next when limit is reached
  useEffect(() => {
    if (
      selectedProfile?.ageRating !== '18' ||
      !duration ||
      (Platform.isMobileWeb() && isFullScreen)
    )
      return;

    const watchedPercentage = (currentTime * 100) / duration;
    const timeRemaining = duration - currentTime;

    const autoNextCueTimeReached = () => {
      const timeRemainingConfig = nextTrack?.displayTimeRemaining || 30;
      if (program.type === ProgramType.PROGRAM) return timeRemaining <= timeRemainingConfig;
      return watchedPercentage >= 96 || timeRemaining <= timeRemainingConfig;
    };

    if (nextTrack && !autoNextTriggered && autoNextCueTimeReached()) {
      setAutoNextTriggered(true);
      setShowNextTrack(true);

      if (Platform.OS === 'web') {
        const { setMemory } = (window as any).useMemory();
        setTimeout(() => setMemory([1, 1]), 0);
      }
    }
  }, [duration, currentTime, autoNextTriggered, isFullScreen]);

  useEffect(() => {
    if (showNextTrack && AUTO_NEXT_TIMEOUT === undefined) {
      AUTO_NEXT_TIMEOUT = setTimeout(() => {
        analytics.triggerMediaAutonextTimerActionEvent({
          programId: program._id,
          programTitle: program.title,
          programAgeRating: program.rating,
          programAdUnit: program.ads?.adunit?.val!,
          programCategory: program.category.map((tag) => tag.name).join(', '),
          programDirector: program.director,
          programGenre: program.genre.map((genre) => genre.name).join(', '),
          programYear: program.year,
        });

        nextTrack?.play();
        clearTimeout(AUTO_NEXT_TIMEOUT);
        AUTO_NEXT_TIMEOUT = undefined;
      }, 10000);
    }

    if (!showNextTrack) {
      clearTimeout(AUTO_NEXT_TIMEOUT);
      AUTO_NEXT_TIMEOUT = undefined;
    }

    return () => {
      clearTimeout(AUTO_NEXT_TIMEOUT);
      AUTO_NEXT_TIMEOUT = undefined;
    };
  }, [showNextTrack]);

  const webTvNextCard =
    showNextTrack && nextTrack ? (
      <LinearGradient
        style={styles.gradientContainer}
        colors={['transparent', '#000000bc']}
        locations={[0.1, 0.4]}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}>
        <View style={styles.webTvContainer}>
          <View
            style={[
              styles.webTvContentContainer,
              { width: calcSizeForDevice(520), gap: calcSizeForTv(15) },
            ]}>
            <View style={[styles.imageContainer, { height: calcSizeForDevice(290) }]}>
              <FastImage
                size="md"
                aspect="16x9"
                source={{ uri: nextTrack.cover }}
                style={styles.fullImage}
              />
            </View>

            <Text style={styles.titleText}>{nextTrack?.title}</Text>

            <Text numberOfLines={2} style={styles.descriptionText}>
              {nextTrack?.description}
            </Text>

            <View style={styles.buttonContainer}>
              <Cancel
                programTitle={nextTrack.program}
                programEpTitle={nextTrack.title}
                onPress={() => setShowNextTrack(false)}
              />
              <Next
                useTVFocus
                onPress={nextTrack.play}
                programTitle={nextTrack.program}
                programEpTitle={nextTrack.title}
              />
            </View>
          </View>
        </View>
      </LinearGradient>
    ) : null;

  const nativeTvNextCard =
    showNextTrack && nextTrack ? (
      <View style={styles.nativeTvContainer}>
        <View
          style={[
            styles.nativeTvContentContainer,
            { width: calcSizeForTv(650), gap: calcSizeForTv(15) },
          ]}>
          <View style={[styles.imageContainer, { height: calcSizeForDevice(362.5) }]}>
            <FastImage
              size="md"
              aspect="16x9"
              source={{ uri: nextTrack.cover }}
              style={styles.fullImage}
            />
          </View>

          <Text style={styles.titleText}>{nextTrack?.title}</Text>

          <Text numberOfLines={2} style={styles.descriptionText}>
            {nextTrack?.description}
          </Text>

          <View style={styles.buttonContainer}>
            <Cancel useTVFocus onPress={() => setShowNextTrack(false)} />
            <Next useTVFocus onPress={nextTrack.play} />
          </View>
        </View>
      </View>
    ) : null;

  const nativeNextCard =
    showNextTrack && nextTrack ? (
      <View style={styles.nativeContainer}>
        <Text style={styles.programText}>{nextTrack?.program}</Text>

        <Text style={styles.nativeTitleText}>{nextTrack?.title}</Text>

        <View style={styles.nativeButtonContainer}>
          <Cancel mini onPress={() => setShowNextTrack(false)} />
          <Next mini useTVFocus onPress={nextTrack.play} />
        </View>
      </View>
    ) : null;

  if (Platform.OS === 'web') {
    return {
      showNextTrack,
      autoNextCard: forceMobileView ? nativeNextCard : webTvNextCard,
    };
  }

  if (Platform.isTV) {
    return {
      showNextTrack,
      autoNextCard: nativeTvNextCard,
    };
  }

  return {
    showNextTrack,
    autoNextCard: nativeNextCard,
  };
}

const styles = StyleSheet.create({
  gradientContainer: {
    width: '100%',
    flex: 1,
  },
  webTvContainer: {
    flex: 1,
    width: '100%',
    padding: 32, // $8 equivalente
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
  },
  webTvContentContainer: {
    // width e gap são definidos inline devido a cálculos dinâmicos
  },
  imageContainer: {
    width: '100%',
    borderRadius: 8, // $md equivalente
    borderWidth: 3,
    borderStyle: 'solid',
    borderColor: 'white',
    // height é definido inline devido a cálculos dinâmicos
  },
  fullImage: {
    width: '100%',
    height: '100%',
  },
  titleText: {
    fontWeight: 'bold',
    fontSize: 20, // $xl equivalente
    color: 'white',
  },
  descriptionText: {
    fontSize: 16, // $md equivalente
    color: 'white',
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 16, // $4 equivalente
    marginTop: 16, // $4 equivalente
    marginBottom: 20,
  },
  nativeTvContainer: {
    flex: 1,
    width: '100%',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
  },
  nativeTvContentContainer: {
    // width e gap são definidos inline devido a cálculos dinâmicos
  },
  nativeContainer: {
    flex: 1,
    width: '100%',
    marginBottom: Platform.isMobileWeb() ? calcSizeForDevice(8) : calcSizeForDevice(30),
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
  },
  programText: {
    fontWeight: 'bold',
    fontSize: 18, // $lg equivalente
    color: 'white',
  },
  nativeTitleText: {
    fontSize: 16, // $md equivalente
    color: 'white',
  },
  nativeButtonContainer: {
    flexDirection: 'row',
    gap: 12, // $3 equivalente
    marginTop: 12, // $3 equivalente
  },
});
